import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router'
import { FirestoreTosite, Maksutapa } from '@lemon/core-firebase/model/tosite'
import { Observable, of as observableOf } from 'rxjs'
import { mergeMap, take } from 'rxjs/operators'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'
import { TositeService } from '../service/tosite/tosite.service'

export interface TositeKatseleComponentData {
  tosite: FirestoreTosite
  tulosta: boolean
}

export interface TositeKatseleComponentExistingData {
  tosite: FirestoreTosite
}
export interface TositeLuoUusiComponentData {
  tiedostot: FileDropEntry[]
  maksutapa?: Maksutapa
}

export interface TositeMuokkaaComponentData {
  tosite: FirestoreTosite | null
  onkoVielaSelvitettavia?: boolean
  /** Muokkaus avattiin selvityspyynnön kortista, joten tallennus vastaa tähän pyyntöön */
  selvityspyynnonAvain?: string
}

@Injectable()
export class TositeLuoUusiComponentDataResolve implements Resolve<TositeLuoUusiComponentData> {
  private existingData: TositeLuoUusiComponentData = null
  private lahdet = ['osto', 'myynti', 'palkka', 'tiliote']

  constructor(private router: Router) {}

  asetaOlemassaolevaData(data: TositeLuoUusiComponentData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<TositeLuoUusiComponentData> {
    if (this.existingData?.tiedostot) {
      return observableOf(this.existingData)
    }
    const lahde = route.url[1]
    if (lahde && this.lahdet.includes(lahde.path)) {
      this.router.navigate(['/tositteet/' + lahde.path])
    } else {
      this.router.navigate(['/tositteet/osto'])
    }
    return observableOf(null)
  }
}

@Injectable()
export class TositeMuokkaaComponentDataResolve implements Resolve<TositeMuokkaaComponentData> {
  private existingData: TositeMuokkaaComponentData = null

  constructor(
    private router: Router,
    private tositeService: TositeService,
  ) {}

  asetaOlemassaolevaData(data: TositeMuokkaaComponentData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<TositeMuokkaaComponentData> {
    const id = route.params['id']
    if (this.existingData !== null && this.existingData.tosite && this.existingData.tosite.avain === id) {
      // Kertakäyttöinen: muuten saman tositteen seuraava avaus näyttäisi tallennusta edeltävän kopion.
      const data = this.existingData
      this.existingData = null
      return observableOf(data)
    } else {
      return this.tositeService.annaKuittiObservable(id).pipe(
        take(1),
        mergeMap(tosite => {
          if (!tosite) {
            this.router.navigate(['/tositteet/osto'])
            return observableOf(null)
          }

          this.asetaOlemassaolevaData({ tosite: tosite })
          const tositeRoute = this.tositeService.getMaksutapaNameForRouter(tosite.maksutapa)
          this.router.navigate(['tositteet', tositeRoute, id])
          return observableOf(null)
        }),
      )
    }
  }
}
@Injectable()
export class TositeKatseleComponentDataResolve implements Resolve<TositeKatseleComponentData> {
  private existingData: TositeKatseleComponentExistingData = null

  constructor(
    private router: Router,
    private tositeService: TositeService,
  ) {}
  asetaOlemassaolevaData(data: TositeKatseleComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<TositeKatseleComponentData> {
    const id = route.params['id']
    const toiminto = route.params['toiminto']
    if (this.existingData != null && this.existingData.tosite && this.existingData.tosite.avain === id) {
      const data = this.existingData
      this.existingData = null
      return this.haeAsetukset(data, toiminto)
    } else {
      return this.tositeService.annaKuittiObservable(id).pipe(
        take(1),
        mergeMap(tosite => {
          if (!tosite) {
            this.router.navigate(['/tositteet/osto'])
            return observableOf(null)
          }
          this.asetaOlemassaolevaData({ tosite: tosite })
          const tositeRoute = this.tositeService.getMaksutapaNameForRouter(tosite.maksutapa)
          this.router.navigate(['tositteet', tositeRoute, id])
          return observableOf(null)
        }),
      )
    }
  }

  private haeAsetukset(existingData: TositeKatseleComponentExistingData, toiminto: string): Observable<TositeKatseleComponentData> {
    const data: TositeKatseleComponentData = {
      tosite: existingData.tosite,
      tulosta: toiminto === 'tulosta',
    }
    return observableOf(data)
  }
}
